import React, { useState, useEffect } from "react";
import { Settings as SettingsIcon, X } from "lucide-react";

interface Settings {
  translatorServiceUrl: string;
  summaryServiceUrl: string;
  userServiceUrl: string;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (settings: Settings) => void;
  initialSettings: Settings;
}

export function SettingsModal({ isOpen, onClose, onSave, initialSettings }: SettingsModalProps) {
  const [translatorUrl, setTranslatorUrl] = useState(initialSettings.translatorServiceUrl);
  const [summaryUrl, setSummaryUrl]       = useState(initialSettings.summaryServiceUrl);
  const [userUrl, setUserUrl]             = useState(initialSettings.userServiceUrl);

  useEffect(() => {
    setTranslatorUrl(initialSettings.translatorServiceUrl);
    setSummaryUrl(initialSettings.summaryServiceUrl);
    setUserUrl(initialSettings.userServiceUrl);
  }, [initialSettings]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({
      translatorServiceUrl: translatorUrl.trim() || "http://localhost:3001",
      summaryServiceUrl:    summaryUrl.trim()    || "http://localhost:3002",
      userServiceUrl:       userUrl.trim()       || "http://localhost:3003",
    });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
      <div
        className="bg-[var(--window-bg)] border border-[var(--border)] rounded-xl max-w-md w-full shadow-2xl overflow-hidden animate-[modal-appear_0.2s_cubic-bezier(0.16,1,0.3,1)] text-[var(--text)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 bg-[var(--surface)] border-b border-[var(--border)]">
          <div className="flex items-center gap-2">
            <SettingsIcon size={18} className="text-[var(--text-dim)]" />
            <h2 className="text-sm font-semibold text-[var(--text)] uppercase tracking-wider font-mono">
              Configuración
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 rounded-md bg-[var(--surface)] hover:bg-[var(--surface-hover)] border border-[var(--border)] text-[var(--text-muted)] hover:text-[var(--text)] flex items-center justify-center transition-all"
            aria-label="Cerrar"
          >
            <X size={14} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 flex flex-col gap-5">

          <p className="text-[10px] font-semibold text-[var(--text-dim)] uppercase tracking-widest font-mono -mb-2">
            URLs de servicios backend
          </p>

          {/* Translator Service URL */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-semibold text-[var(--text-dim)] uppercase tracking-widest font-mono flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-purple-400"></span>
              Translator Service
            </label>
            <input
              type="text"
              value={translatorUrl}
              onChange={(e) => setTranslatorUrl(e.target.value)}
              placeholder="https://…execute-api.amazonaws.com"
              className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg py-2.5 px-3 text-xs text-[var(--text)] placeholder-[var(--text-dim)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all font-mono"
            />
            <p className="text-[10px] text-[var(--text-dim)] leading-normal mt-0.5">
              POST /meeting · POST /meeting/transcript · GET /meetings · GET /meetings/&#123;id&#125;/transcripts · PUT /meetings/&#123;id&#125;
            </p>
          </div>

          {/* Summary Service URL */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-semibold text-[var(--text-dim)] uppercase tracking-widest font-mono flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400"></span>
              Summary Service
            </label>
            <input
              type="text"
              value={summaryUrl}
              onChange={(e) => setSummaryUrl(e.target.value)}
              placeholder="https://…execute-api.amazonaws.com"
              className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg py-2.5 px-3 text-xs text-[var(--text)] placeholder-[var(--text-dim)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all font-mono"
            />
            <p className="text-[10px] text-[var(--text-dim)] leading-normal mt-0.5">
              POST /summarize · POST /meeting/email
            </p>
          </div>

          {/* User Service URL */}
          <div className="flex flex-col gap-1.5">
            <label className="text-[10px] font-semibold text-[var(--text-dim)] uppercase tracking-widest font-mono flex items-center gap-1.5">
              <span className="inline-block w-2 h-2 rounded-full bg-sky-400"></span>
              User Service
            </label>
            <input
              type="text"
              value={userUrl}
              onChange={(e) => setUserUrl(e.target.value)}
              placeholder="https://…execute-api.amazonaws.com"
              className="w-full bg-[var(--surface)] border border-[var(--border)] rounded-lg py-2.5 px-3 text-xs text-[var(--text)] placeholder-[var(--text-dim)] outline-none focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent)]/20 transition-all font-mono"
            />
            <p className="text-[10px] text-[var(--text-dim)] leading-normal mt-0.5">
              GET /user/profile · PATCH /user/profile
            </p>
          </div>

          {/* Footer Actions */}
          <div className="flex items-center justify-end gap-3 mt-2 pt-4 border-t border-[var(--border)]">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-xs font-medium text-[var(--text-muted)] hover:text-[var(--text)] transition-all bg-transparent hover:bg-[var(--surface)] rounded-lg"
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-xs font-semibold text-white bg-[var(--accent)] hover:bg-[#006ee6] active:scale-95 rounded-lg shadow-lg transition-all"
            >
              Guardar Cambios
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
